import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Tooltip from '@src/components/Tooltip';
import {
  VolumeUpRounded as VolumeUpRoundedIcon,
  VolumeOff as VolumeOffIcon,
  GraphicEqRounded as GraphicEqRoundedIcon,
  MovieRounded as MovieRoundedIcon,
} from '@mui/icons-material';
import actions from '@src/redux/actions';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { HISTORY_LABEL, HISTORY_SCOPE } from '@src/constants/history';
import {
  PROJECT_PRODUCT,
  VALID_VOLUME,
  VOLUME_TRACK,
} from '@src/constants/project';
import { COLOR } from '@src/styles/color';
import {
  StyledVolumeMixer,
  StyledMixerColumn,
  StyledMixerIconButton,
  StyledMixerSlider,
} from './index.style';

const VolumeMixerPopover = ({
  open,
  anchorEl,
  onClose,
  audioVolume,
  onChangeAudioVolume,
}) => {
  const dispatch = useDispatch();
  const videoVolume = useSelector((state) => state.project.volumes?.media);
  const videoVolumePercent = Math.round(
    Math.max(0, Math.min(1, videoVolume ?? 1)) * VALID_VOLUME.MAX,
  );

  // The video level is project state the dubbing undo/redo records, so it has to
  // open an entry like the timeline's mixer does — otherwise undoing an older
  // edit would silently snap this level back to what it was then. Dubbing only:
  // that is the only scope with history.
  const user = useSelector((state) => state.auth.user);
  const product = useSelector((state) => state.project.product);
  const { getFeatureValue } = useFeatureFlags();
  const undoRedoEnabled =
    product === PROJECT_PRODUCT.DUBBING &&
    getFeatureValue(FEATURE_KEYS.PROJECT_DUBBING_UNDO_REDO, {
      userId: user?.id,
      email: user?.email,
    });
  const historyOptions = undoRedoEnabled
    ? {
        history: {
          commit: true,
          scope: HISTORY_SCOPE.DUBBING,
          label: HISTORY_LABEL.CHANGE_TRACK_VOLUME,
        },
      }
    : {};
  // CHANGE_TRACK_VOLUME is deferred, so the drag coalesces into one entry; this
  // closes it on pointer release / after a mute click.
  const commitVolume = () => {
    if (undoRedoEnabled) dispatch(actions.history.flush(HISTORY_SCOPE.DUBBING));
  };

  const setVideoVolume = (value) => {
    const clamped = Math.max(0, Math.min(VALID_VOLUME.MAX, value));
    dispatch(
      actions.project.setTrackVolume(
        VOLUME_TRACK.MEDIA,
        clamped / VALID_VOLUME.MAX,
        historyOptions,
      ),
    );
  };

  const handleToggleAudioMute = () => {
    onChangeAudioVolume(audioVolume > 0 ? VALID_VOLUME.MIN : VALID_VOLUME.MAX);
  };

  const handleToggleVideoMute = () => {
    setVideoVolume(
      videoVolumePercent > 0 ? VALID_VOLUME.MIN : VALID_VOLUME.MAX,
    );
    commitVolume();
  };

  return (
    <StyledVolumeMixer
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <div className="mixer-wrapper">
        <StyledMixerColumn>
          <StyledMixerIconButton onClick={handleToggleAudioMute}>
            {audioVolume > 0 ? (
              <VolumeUpRoundedIcon sx={{ color: COLOR.indigo.default }} />
            ) : (
              <VolumeOffIcon sx={{ color: COLOR.indigo.default }} />
            )}
          </StyledMixerIconButton>
          <StyledMixerSlider
            orientation="vertical"
            value={audioVolume}
            min={VALID_VOLUME.MIN}
            max={VALID_VOLUME.MAX}
            step={1}
            onChange={(_, value) => onChangeAudioVolume(value)}
          />
          <Tooltip title="Audio" placement="bottom" arrow>
            <StyledMixerIconButton disableRipple>
              <GraphicEqRoundedIcon sx={{ color: COLOR.indigo[80] }} />
            </StyledMixerIconButton>
          </Tooltip>
        </StyledMixerColumn>

        <StyledMixerColumn>
          <StyledMixerIconButton onClick={handleToggleVideoMute}>
            {videoVolumePercent > 0 ? (
              <VolumeUpRoundedIcon sx={{ color: COLOR.indigo.default }} />
            ) : (
              <VolumeOffIcon sx={{ color: COLOR.indigo.default }} />
            )}
          </StyledMixerIconButton>
          <StyledMixerSlider
            orientation="vertical"
            value={videoVolumePercent}
            min={VALID_VOLUME.MIN}
            max={VALID_VOLUME.MAX}
            step={1}
            onChange={(_, value) => setVideoVolume(value)}
            onChangeCommitted={commitVolume}
          />
          <Tooltip title="Video" placement="bottom" arrow>
            <StyledMixerIconButton disableRipple>
              <MovieRoundedIcon sx={{ color: COLOR.indigo[80] }} />
            </StyledMixerIconButton>
          </Tooltip>
        </StyledMixerColumn>
      </div>
    </StyledVolumeMixer>
  );
};

export default VolumeMixerPopover;
